import React from 'react';
import { FONTS } from '../../styles/tokens';

export type DeployStepId = 'name' | 'upload' | 'secrets' | 'review' | 'building';

interface DeployStepperProps {
  currentStep: DeployStepId;
  textPrimary: string;
  textSecondary: string;
  accent: string;
  border: string;
}

interface StepDefinition {
  id: DeployStepId;
  label: string;
}

const STEPS: readonly StepDefinition[] = [
  { id: 'name', label: 'Details' },
  { id: 'upload', label: 'Upload' },
  { id: 'secrets', label: 'Secrets' },
  { id: 'review', label: 'Review' },
  { id: 'building', label: 'Deploy' },
];

/**
 * Horizontal stepper showing where the user is in the deploy flow. Purely
 * presentational — the parent owns the active step.
 */
export function DeployStepper(props: DeployStepperProps): React.JSX.Element {
  const activeIndex = findStepIndex(props.currentStep);
  return (
    <div className="vx-stepper" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
      {STEPS.map((step, i) => (
        <StepDot
          key={step.id}
          index={i}
          label={step.label}
          state={classifyState(i, activeIndex)}
          textPrimary={props.textPrimary}
          textSecondary={props.textSecondary}
          accent={props.accent}
          border={props.border}
          showConnector={i > 0}
        />
      ))}
    </div>
  );
}

interface StepDotProps {
  index: number;
  label: string;
  state: 'done' | 'active' | 'upcoming';
  textPrimary: string;
  textSecondary: string;
  accent: string;
  border: string;
  showConnector: boolean;
}

/** One numbered-dot + label + the connector line to the previous dot. */
function StepDot(props: StepDotProps): React.JSX.Element {
  const connectorColor = props.state === 'upcoming' ? props.border : props.accent;
  const dotBg = props.state === 'upcoming' ? 'transparent' : props.accent;
  const dotBorder = props.state === 'upcoming' ? props.border : props.accent;
  const dotColor = props.state === 'upcoming' ? props.textSecondary : '#FFFFFF';
  const labelColor = props.state === 'upcoming' ? props.textSecondary : props.textPrimary;

  return (
    <>
      {props.showConnector && (
        <div style={{ height: '1px', flex: 1, backgroundColor: connectorColor }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div
          style={{
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            border: `1px solid ${dotBorder}`,
            backgroundColor: dotBg,
            color: dotColor,
            display: 'grid',
            placeItems: 'center',
            fontFamily: FONTS.heading,
            fontSize: '11px',
          }}
        >
          {props.index + 1}
        </div>
        <span
          className="vx-stepper-label"
          style={{
            fontFamily: FONTS.heading,
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: labelColor,
          }}
        >
          {props.label}
        </span>
      </div>
    </>
  );
}

/** Index of the currently-active step within the predefined STEPS list. */
function findStepIndex(id: DeployStepId): number {
  for (let i = 0; i < STEPS.length; i++) {
    if (STEPS[i].id === id) return i;
  }
  return 0;
}

/** Classify a step by position relative to the active index. */
function classifyState(stepIndex: number, activeIndex: number): 'done' | 'active' | 'upcoming' {
  if (stepIndex < activeIndex) return 'done';
  if (stepIndex === activeIndex) return 'active';
  return 'upcoming';
}
